/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ImportModal, ImportModalDto } from 'src/core/components/ImportModal';
import { Page } from 'src/core/components/Page';
import { RenameModal, RenameModalDto } from 'src/core/components/RenameModal';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useDebounce } from 'src/core/hooks/useDebounce';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { FileType } from 'src/core/types/FileType';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { routeTable } from 'src/core/utils/routeTable';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { appVersionPageMessages } from 'src/luis/appShell/managePane/appVersionsPage/appVersionPageMessages';
import { AppVersionTable } from 'src/luis/appShell/managePane/appVersionsPage/components/AppVersionTable';
import { AppVersionTableCommandBar } from 'src/luis/appShell/managePane/appVersionsPage/components/AppVersionTableCommandBar';
import { VersionCloneModal, VersionCloneModalDto } from 'src/luis/appShell/managePane/appVersionsPage/components/VersionCloneModal';
import { ConversationPublishSlotStore } from 'src/luis/core/stores/ConversationPublishSlotStore';
import { useCurrentVersionedAppId } from 'src/luis/modules/apps/hooks/useCurrentVersionedAppId';
import { AppVersionStore } from 'src/luis/types/stores/apps/AppVersionStore';

/**
 * The page displaying and managing all app versions.
 */
export const AppVersionsPage = observer(() => {
    const { localize } = useLocalization();
    const [query, setQuery] = React.useState('');
    const debouncedQuery = useDebounce<string>(query, 500);
    const [modalsState, setModalsState] = useUpdateState(defaultModalsState);
    const { selection, selectedItems } = useSelection<AppVersionStore>();
    const { dispatcher, rootStore } = useGlobalState();
    const { appId, versionId } = useCurrentVersionedAppId();
    const currentAppStore = rootStore.currentLuisAppStore;
    const { items: appVersions, isLoading } = currentAppStore.appVersionStoreHouse;
    const [fileTypeToImport, setFileTypeToImport] = React.useState<FileType>('json');

    const [sortKey, setSortKey] = React.useState<SortKeyType<AppVersionStore>>({ key: 'id', order: 'asc' });
    const filteredAppVersions = orderBy(substringSearchStrategy(appVersions, debouncedQuery, 'id'), [sortKey.key], [sortKey.order]);
    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(filteredAppVersions);

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canDeleteData } = mapUserRoleToUserActions(userRole);

    /**
     * If the query changes and has a value, then load
     * the apps by chunking. Else stop any occurring
     * chunking processes if any, and reload the first
     * page of the applications.
     */
    React.useEffect(() => {
        if (debouncedQuery.trim().length === 0) {
            dispatcher.dispatch('cancelAppVersionsChunking');
            dispatcher.dispatch('loadAppVersions', { context: { forceRefresh: true } });
        } else {
            dispatcher.dispatch('loadAppVersionsByChunking');
        }
    }, [debouncedQuery]);

    const tracker = useProgressTracker({
        onComplete: () => {
            setModalsState(defaultModalsState);
            selection.setAllSelected(false);
        }
    });

    /**
     * Activate (switch) the currently selected version.
     */
    const handleActivate = () => {
        const versionToActivate = selectedItems[0].id;

        dispatcher.dispatch('activateAppVersion', {
            redirectUrl: routeTable.luis.versionsPage(appId, versionToActivate),
            context: { versionId: selectedItems[0].id },
            tracker,
            toastData: {
                running: { title: localize(appVersionPageMessages.activateVersionToastStart) },
                completed: { title: localize(appVersionPageMessages.activateVersionToastSuccess) },
                failed: { title: localize(appVersionPageMessages.activateVersionToastFailed) }
            }
        });
    };

    /**
     * A callback function for exporting an app version.
     */
    const handleExport = (fileType: FileType) => {
        dispatcher.dispatch('exportAppVersion', {
            fileType,
            tracker,
            toastData: {
                running: { title: localize(appVersionPageMessages.exportVersionToastStart) },
                completed: { title: localize(appVersionPageMessages.exportVersionToastSuccess) },
                failed: { title: localize(appVersionPageMessages.exportVersionToastFailed) }
            },
            context: { versionId: selectedItems[0].id }
        });
    };

    /**
     * A callback function for importing an app version.
     */
    const handleImport = (data: ImportModalDto) => {
        dispatcher.dispatch('importAppVersion', {
            file: data.file,
            optionalVersionId: data.name,
            fileType: fileTypeToImport,
            tracker,
            toastData: {
                running: { title: localize(appVersionPageMessages.importVersionToastStart) },
                completed: { title: localize(appVersionPageMessages.importVersionToastSuccess) },
                failed: { title: localize(appVersionPageMessages.importVersionToastFailed) }
            }
        });
    };

    /**
     * A callback function for renaming an app version.
     */
    const handleRename = (data: RenameModalDto) => {
        dispatcher.dispatch('renameAppVersion', {
            newVersion: data.name,
            redirectUrl: routeTable.luis.versionsPage(appId, data.name),
            context: { versionId: selectedItems[0].id },
            tracker,
            toastData: {
                running: { title: localize(appVersionPageMessages.renameVersionToastStart) },
                completed: { title: localize(appVersionPageMessages.renameVersionToastSuccess) },
                failed: { title: localize(appVersionPageMessages.renameVersionToastFailed) }
            }
        });
    };

    /**
     * A callback function for cloning an app version.
     */
    const handleClone = (data: VersionCloneModalDto) => {
        dispatcher.dispatch('cloneAppVersion', {
            newVersion: data.name,
            redirectUrl: routeTable.luis.versionsPage(appId, data.name),
            context: { versionId: selectedItems[0].id },
            tracker,
            toastData: {
                running: { title: localize(appVersionPageMessages.cloneVersionToastStart) },
                completed: { title: localize(appVersionPageMessages.cloneVersionToastSuccess) },
                failed: { title: localize(appVersionPageMessages.cloneVersionToastFailed) }
            }
        });
    };

    /**
     * A callback function for deleting app version(s).
     */
    const handleDelete = () => {
        dispatcher.dispatch('deleteAppVersions', {
            versionIdsToDelete: selectedItems.map(selectedItem => selectedItem.id),
            tracker,
            toastData: {
                running: { title: localize(appVersionPageMessages.deleteVersionToastStart) },
                completed: { title: localize(appVersionPageMessages.deleteVersionToastSuccess) },
                failed: { title: localize(appVersionPageMessages.deleteVersionToastFailed) }
            }
        });
    };

    return (
        <Page pageName="AppVersionsPage" size="container-fluid" title={localize(appVersionPageMessages.browserTitle)}>
            <EditableTitle title={localize(appVersionPageMessages.pageTitle)} />

            <AppVersionTableCommandBar
                selectedItems={selectedItems}
                activeVersionId={versionId}
                appVersionsCount={currentAppStore.appVersionStoreHouse.items.length}
                isVersionTrained={currentAppStore.currentAppVersionStore.isTrained}
                onImportAppVersion={fileType => {
                    setFileTypeToImport(fileType);
                    setModalsState({ isImportAppVersionModalHidden: false });
                }}
                onRenameAppVersion={() => setModalsState({ isRenameAppVersionModalHidden: false })}
                onCloneAppVersion={() => setModalsState({ isCloneAppVersionModalHidden: false })}
                onDeleteAppVersions={() => setModalsState({ isDeleteAppVersionModalHidden: false })}
                onExportAppVersion={handleExport}
                onActivateAppVersion={handleActivate}
                query={query}
                onQueryChange={setQuery}
                userRole={userRole}
                displayedUserRole={displayedUserRole}
            />

            <AppVersionTable
                sortKey={sortKey}
                items={pageItems}
                selection={selection}
                isLoading={isLoading}
                areItemsFiltered={Boolean(query)}
                overallItemsCount={filteredAppVersions.length}
                emptyPlaceholder={localize(appVersionPageMessages.emptyTablePlaceholder)}
                onDeleteKeyUp={() => canDeleteData && setModalsState({ isDeleteAppVersionModalHidden: false })}
                versionsPublishState={{
                    activeVersionId: currentAppStore.activeVersion,
                    productionSlotMetaData: {
                        id: (currentAppStore.publishSlots.production as ConversationPublishSlotStore)?.versionId,
                        date: currentAppStore.publishSlots.production?.lastPublishedDate
                    },
                    stagingSlotMetaData: {
                        id: (currentAppStore.publishSlots.staging as ConversationPublishSlotStore)?.versionId,
                        date: currentAppStore.publishSlots.staging?.lastPublishedDate
                    }
                }}
                onSortColumn={c => {
                    setSortKey({ key: c.key as keyof AppVersionStore, order: c.isSortedDescending ? 'asc' : 'desc' });
                    selection.setAllSelected(false);
                }}
            />

            <TablePagination pageCount={pageCount} currentPage={currentPage} onPageChange={p => setCurrentPage(p.selected)} />

            <ImportModal
                onSubmit={handleImport}
                fileType={fileTypeToImport}
                disabled={tracker.isRunning}
                hidden={modalsState.isImportAppVersionModalHidden}
                onDismiss={_ => setModalsState({ isImportAppVersionModalHidden: true })}
                dialogContentProps={{ title: localize(appVersionPageMessages.importAppVersion) }}
            />

            <RenameModal
                onSubmit={handleRename}
                disabled={tracker.isRunning}
                name={selectedItems[0] ? selectedItems[0].id : ''}
                hidden={modalsState.isRenameAppVersionModalHidden}
                onDismiss={_ => setModalsState({ isRenameAppVersionModalHidden: true })}
                dialogContentProps={{ title: localize(appVersionPageMessages.renameAppVersion) }}
            />

            <VersionCloneModal
                disabled={tracker.isRunning}
                hidden={modalsState.isCloneAppVersionModalHidden}
                onDismiss={_ => setModalsState({ isCloneAppVersionModalHidden: true })}
                onSubmit={handleClone}
            />

            <DeletePromptModal
                disabled={tracker.isRunning}
                hidden={modalsState.isDeleteAppVersionModalHidden}
                dialogContentProps={{
                    title: localize(appVersionPageMessages.deleteAppVersionModalTitle),
                    subText: localize(appVersionPageMessages.deleteAppVersionModalDescription)
                }}
                onDismiss={_ => setModalsState({ isDeleteAppVersionModalHidden: true })}
                onDelete={handleDelete}
            />
        </Page>
    );
});

const defaultModalsState = {
    isImportAppVersionModalHidden: true,
    isRenameAppVersionModalHidden: true,
    isCloneAppVersionModalHidden: true,
    isDeleteAppVersionModalHidden: true
};
